import Ionicons from "@expo/vector-icons/Ionicons";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { useTranslation } from "react-i18next";

import { billingApi, practiceApi } from "@/api";
import { Radius, Spacing, type ThemeColors } from "@/constants/theme";
import { useTheme } from "@/hooks/use-theme";
import { useAuthStore } from "@/state/auth-store";
import i18n from "@/i18n";

const FONT_FAMILY = Platform.select({
  ios: "Avenir Next",
  android: "sans-serif",
  default: undefined,
});

/** Seconds before a successful checkout auto-returns home. */
const SUCCESS_AUTO_REDIRECT_SECONDS = 30;

/**
 * Stripe Checkout return screen. Stripe redirects here with:
 *   /checkout?status=success|cancelled&kind=plus|pack&session_id=cs_...
 *
 * Success  → confirmation + "Back to home" + a {n}s auto-redirect home.
 * Cancelled → "Payment not completed" + "Try again" (to the right screen) + home.
 *
 * IMPORTANT: nothing here trusts the query string as proof of payment. The
 * entitlement is confirmed by asking the server to verify the `session_id`
 * (POST /billing/confirm), which checks the session is paid AND owned by the
 * authenticated user before crediting. A hand-crafted URL therefore grants
 * nothing and shows no fabricated amount.
 */
export default function CheckoutReturnScreen() {
  useTranslation();
  const router = useRouter();
  const c = useTheme();
  const styles = useMemo(() => createStyles(c), [c]);

  const params = useLocalSearchParams<{
    status?: string;
    kind?: string;
    session_id?: string;
    subscription_id?: string;
  }>();
  const isSuccess = params.status !== "cancelled";
  const kind = params.kind === "pack" ? "pack" : "plus";
  const sessionId =
    typeof params.session_id === "string" ? params.session_id : "";
  // Set when the in-app checkout had to leave the page for a 3DS challenge.
  // Same trust model as session_id: it's an id the server verifies, not proof.
  const subscriptionId =
    typeof params.subscription_id === "string" ? params.subscription_id : "";

  const refreshUser = useAuthStore((s) => s.refreshUser);

  const [seconds, setSeconds] = useState(SUCCESS_AUTO_REDIRECT_SECONDS);
  // Server-confirmed practice-reply balance (null until confirmed). We only ever
  // show a number the server vouches for — never a value from the URL.
  const [replies, setReplies] = useState<number | null>(null);
  // Whether a payment was server-verified as paid on this return.
  const [verifiedPaid, setVerifiedPaid] = useState(false);
  // Whether the server round-trip is still in flight. Starts true only when
  // there's actually something to verify (success + a session id).
  const [confirming, setConfirming] = useState(
    () => isSuccess && (sessionId !== "" || subscriptionId !== ""),
  );
  // Guard so the countdown and a manual tap can't both navigate.
  const navigatedRef = useRef(false);

  const goHome = useCallback(() => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    router.replace("/(tabs)/home");
  }, [router]);

  const tryAgain = useCallback(() => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    // Send the user back to where they can retry the same purchase.
    router.replace(kind === "pack" ? "/practice" : "/pricing");
  }, [router, kind]);

  // Auto-return countdown — only once the payment is server-verified. The
  // updater ONLY updates state; navigation happens in the effect below when
  // seconds hit 0, so we never call router.replace() from inside a render.
  useEffect(() => {
    if (!verifiedPaid) return;

    const id = setInterval(() => {
      setSeconds((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);

    return () => clearInterval(id);
  }, [verifiedPaid]);

  // Navigate home when the countdown reaches 0 (kept out of the state updater).
  useEffect(() => {
    if (verifiedPaid && seconds === 0) goHome();
  }, [verifiedPaid, seconds, goHome]);

  // Success: verify the session with the server, which fulfils it if paid
  // (idempotent) and is the single source of truth. This also credits reliably
  // even if the Stripe webhook is delayed or, in local dev, not forwarded.
  useEffect(() => {
    // Nothing to verify (cancelled, or a hand-typed URL with no id): `confirming`
    // already starts false, so we credit nothing and claim nothing.
    if (!isSuccess || (!sessionId && !subscriptionId)) return;

    let active = true;
    void (async () => {
      try {
        // Returning from the in-app checkout's 3DS redirect. The server checks
        // the subscription belongs to us and is genuinely active before we
        // claim anything.
        if (!sessionId) {
          const summary = await billingApi.syncSubscription(subscriptionId);
          if (!active) return;
          if (summary.active) {
            setVerifiedPaid(true);
            await refreshUser();
          }
          return;
        }

        const result = await billingApi.confirm(sessionId);
        if (!active) return;
        if (result.status === "paid") {
          setVerifiedPaid(true);
          if (result.kind === "pack") {
            const credits = await practiceApi.credits();
            if (active) setReplies(credits.remainingReplies);
          } else {
            await refreshUser();
          }
        }
      } catch {
        // Leave verifiedPaid false → we show a neutral, non-committal message.
      } finally {
        if (active) setConfirming(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [isSuccess, sessionId, subscriptionId, kind, refreshUser]);

  // Drive the whole screen off a single, server-truth-based phase.
  const phase: "cancelled" | "confirming" | "paid" | "unverified" = !isSuccess
    ? "cancelled"
    : confirming
      ? "confirming"
      : verifiedPaid
        ? "paid"
        : "unverified";

  let title: string;
  let subtitle: string;
  if (phase === "confirming") {
    title = i18n.t("billing.checkout.confirmingTitle");
    subtitle = i18n.t("billing.checkout.confirmingBody");
  } else if (phase === "paid") {
    title = i18n.t("billing.checkout.successTitle");
    if (kind === "pack") {
      subtitle =
        replies !== null
          ? i18n.t("billing.checkout.practiceReplies", { count: replies })
          : i18n.t("billing.checkout.purchaseConfirmed");
    } else {
      subtitle = i18n.t("billing.checkout.plusActive");
    }
  } else if (phase === "cancelled") {
    title = i18n.t("billing.checkout.cancelledTitle");
    subtitle = i18n.t("billing.checkout.cancelledBody");
  } else {
    // Success param but the server did not confirm a paid session (e.g. a
    // hand-crafted URL, or a not-yet-paid session). Claim nothing.
    title = i18n.t("billing.checkout.unverifiedTitle");
    subtitle = i18n.t("billing.checkout.unverifiedBody");
  }

  const showSuccessUi = phase === "paid";
  const showConfirming = phase === "confirming";

  return (
    <SafeAreaView style={styles.safe}>
      <StatusBar style={c.text === "#F5F8FC" ? "light" : "dark"} />

      <View style={styles.content}>
        {showConfirming ? (
          <ActivityIndicator
            size="large"
            color={c.tint}
            style={styles.iconCircle}
          />
        ) : (
          <View
            style={[
              styles.iconCircle,
              {
                backgroundColor: showSuccessUi ? c.successTint : c.dangerTint,
              },
            ]}
          >
            <Ionicons
              name={showSuccessUi ? "checkmark-circle" : "close-circle"}
              size={64}
              color={showSuccessUi ? c.success : c.danger}
            />
          </View>
        )}

        <Text style={styles.title}>{title}</Text>
        <Text style={styles.subtitle}>{subtitle}</Text>

        {showConfirming ? null : showSuccessUi ? (
          <>
            <Pressable
              accessibilityRole="button"
              onPress={goHome}
              style={({ pressed }) => [
                styles.primaryBtn,
                pressed && styles.pressed,
              ]}
            >
              <Text style={styles.primaryBtnText}>
                {i18n.t("billing.checkout.backHome")}
              </Text>
            </Pressable>
            <Text style={styles.countdown}>
              {i18n.t("billing.checkout.returning", { count: seconds })}
            </Text>
          </>
        ) : (
          <>
            <Pressable
              accessibilityRole="button"
              onPress={tryAgain}
              style={({ pressed }) => [
                styles.primaryBtn,
                pressed && styles.pressed,
              ]}
            >
              <Text style={styles.primaryBtnText}>
                {i18n.t("billing.checkout.tryAgain")}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              onPress={goHome}
              style={({ pressed }) => [
                styles.secondaryBtn,
                pressed && styles.pressed,
              ]}
            >
              <Text style={styles.secondaryBtnText}>
                {i18n.t("billing.checkout.backHome")}
              </Text>
            </Pressable>
          </>
        )}
      </View>
    </SafeAreaView>
  );
}

function createStyles(c: ThemeColors) {
  return StyleSheet.create({
    safe: { flex: 1, backgroundColor: c.background },
    content: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingHorizontal: Spacing.xl,
      gap: Spacing.md,
    },
    iconCircle: {
      width: 112,
      height: 112,
      borderRadius: 56,
      alignItems: "center",
      justifyContent: "center",
      marginBottom: Spacing.sm,
    },
    title: {
      fontFamily: FONT_FAMILY,
      fontSize: 24,
      fontWeight: "800",
      color: c.text,
      textAlign: "center",
    },
    subtitle: {
      fontFamily: FONT_FAMILY,
      fontSize: 16,
      lineHeight: 23,
      color: c.textSecondary,
      textAlign: "center",
      maxWidth: 340,
      marginBottom: Spacing.md,
    },
    primaryBtn: {
      backgroundColor: c.tint,
      paddingVertical: 15,
      paddingHorizontal: Spacing.xl,
      borderRadius: Radius.lg,
      alignItems: "center",
      alignSelf: "center",
      width: "100%",
      maxWidth: 340,
    },
    primaryBtnText: {
      fontFamily: FONT_FAMILY,
      color: c.onTint,
      fontSize: 16,
      fontWeight: "700",
    },
    secondaryBtn: {
      paddingVertical: 13,
      paddingHorizontal: Spacing.xl,
      borderRadius: Radius.lg,
      alignItems: "center",
      alignSelf: "center",
      width: "100%",
      maxWidth: 340,
      borderWidth: 1,
      borderColor: c.border,
    },
    secondaryBtnText: {
      fontFamily: FONT_FAMILY,
      color: c.text,
      fontSize: 16,
      fontWeight: "600",
    },
    countdown: {
      fontFamily: FONT_FAMILY,
      fontSize: 13,
      color: c.textMuted,
      marginTop: Spacing.xs,
    },
    pressed: { opacity: 0.85 },
  });
}
